/**
 * Ranks / Season 1 — beta-phase leaderboard surface.
 *
 * Shape:
 *   1. Tier hero — your current rank + tier emoji + progress toward next tier.
 *   2. Stat row — points, trivias played, streak.
 *   3. Topic tabs — Global + one per topic you've earned points in.
 *   4. Leaderboard list — top 50 for the selected tab.
 *   5. Badges grid — all 6 badges, earned ones highlighted.
 *
 * Data fetched in parallel from /leaderboard/me + /leaderboard/global
 * on first paint. Switching to a topic tab fires /leaderboard/topic/{id}
 * and swaps just the list — the hero + stats stay put.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';

let currentMe = null;     // last /leaderboard/me payload
let currentTab = 'global'; // 'global' or 'friends' or a category_id
let currentWindow = 'weekly'; // default; reset every mount in renderRanks

const WINDOWS = [
  ['all_time', 'All-time'],
  ['monthly', 'This Month'],
  ['weekly', 'This Week'],
  ['last_month', 'Last Month'],
];

export async function renderRanks(root) {
  // Always land on This Week — the freshest board is the one people
  // are most likely to want. All-time is one tap away.
  currentWindow = 'weekly';
  currentTab = 'global';

  root.innerHTML = `
    <div class="appbar">
      <div class="appbar-title">Ranks</div>
      <div class="appbar-spacer"></div>
      <span class="tag vio" id="season-tag">SEASON 1</span>
    </div>
    <div class="pad" id="ranks-page">
      <div class="skel"></div>
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  const me = auth.getMe();
  if (!me) { navigate('/login'); return; }
  await loadAndRender();
}

async function loadAndRender() {
  const page = document.getElementById('ranks-page');
  let meData, globalBoard;
  try {
    [meData, globalBoard] = await Promise.all([
      api('/leaderboard/me'),
      api(`/leaderboard/global?limit=50&window=${currentWindow}`),
    ]);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) { navigate('/login'); return; }
    toast.fromError(err, 'Could not load leaderboard.');
    page.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }
  currentMe = meData;
  document.getElementById('season-tag').textContent = `${meData.season.toUpperCase()} · BETA`;

  page.innerHTML = `
    ${tierHero(meData)}
    ${statRow(meData)}
    ${windowRow()}
    ${tabRow(meData)}
    <div id="board-list">${renderBoard(globalBoard.entries, meData.user_id)}</div>
    ${badgesGrid(meData.badges)}
  `;
  wireWindowClicks();
  wireTabClicks();
}

// ---- hero + stats ----------------------------------------------------------

function tierHero(me) {
  // Use the brand-violet hero treatment so the screen reads consistently with
  // the rest of the app. The tier emoji doubles as a visual identity for each
  // ladder rung (🥉 🥈 🥇 💠 💎).
  const nextLine = me.next_tier
    ? `${me.points_to_next_tier} pts to ${escape(me.next_tier.label)} ${escape(me.next_tier.emoji)}`
    : 'Top of the ladder. Don\'t look down.';
  const rankLine = me.rank
    ? `#${me.rank} globally`
    : 'Play a live trivia to enter the ladder';
  return `
    <div class="hero violet center" style="padding:18px;">
      <div style="font-size:34px;">${escape(me.tier.emoji)}</div>
      <div style="font-family:var(--font-display);font-size:20px;font-weight:600;margin-top:4px;">
        ${escape(me.tier.label)} · ${me.points} pts
      </div>
      <div style="font-size:12px;font-weight:500;opacity:0.9;margin-top:4px;">
        ${escape(rankLine)}
      </div>
      ${me.next_tier ? `
        <div class="xpbar" style="margin:10px 18px 4px;">
          <i style="width:${tierProgressPct(me)}%;"></i>
        </div>
      ` : ''}
      <div style="font-size:11px;font-weight:500;opacity:0.85;">
        ${escape(nextLine)}
      </div>
    </div>
  `;
}

function tierProgressPct(me) {
  if (!me.next_tier) return 100;
  const from = me.tier.min_points;
  const to = me.next_tier.min_points;
  if (to <= from) return 100;
  const pct = ((me.points - from) / (to - from)) * 100;
  return Math.max(2, Math.min(100, Math.round(pct)));
}

function statRow(me) {
  // `duels_played` was added when 1-v-1 duel points started counting
  // toward the leaderboard. Fallback to 0 so an older cached /me payload
  // (or a briefly-stale service worker) doesn't render `undefined`.
  const duels = Number(me.duels_played || 0);
  return `
    <div class="card">
      <div class="row">
        <div class="stat">
          <div class="v" style="color:var(--flame);">${me.streak}</div>
          <div class="k">Streak</div>
        </div>
        <div class="stat">
          <div class="v">${me.trivias_played}</div>
          <div class="k">Trivias</div>
        </div>
        <div class="stat">
          <div class="v">${duels}</div>
          <div class="k">Duels</div>
        </div>
        <div class="stat">
          <div class="v">${me.correct}</div>
          <div class="k">Correct</div>
        </div>
      </div>
    </div>
  `;
}

// ---- window strip (All-time / Monthly / Weekly) ---------------------------

function windowRow() {
  return `
    <h3 class="sec">Leaderboard</h3>
    <div class="row" style="gap:6px;margin-bottom:8px;">
      ${WINDOWS.map(
        ([key, label]) => `
        <button type="button"
                class="pill window-btn ${currentWindow === key ? 'on' : ''}"
                data-window="${escape(key)}"
                style="flex:1;text-align:center;">
          ${escape(label)}
        </button>
      `,
      ).join('')}
    </div>
  `;
}


// ---- tabs (Global / Friends / Topics) -------------------------------------

function tabRow(me) {
  // First tab is Global; the rest are topics the user has actually earned
  // points in. Heavy players can rack up 10+ topic pills — force
  // horizontal scroll (no wrap, no scrollbar arrows on mobile) so the
  // strip never crowds the leaderboard list below.
  const topicTabs = (me.topics || [])
    .map((t) => `
      <button type="button" class="pill tab-btn ${currentTab === t.category_id ? 'on' : ''}"
              data-tab="${escape(t.category_id)}" style="flex:0 0 auto;white-space:nowrap;">
        ${escape(t.name)}
      </button>
    `)
    .join('');
  return `
    <div class="row" style="gap:6px;overflow-x:auto;flex-wrap:nowrap;-webkit-overflow-scrolling:touch;padding-bottom:4px;scrollbar-width:none;">
      <button type="button" class="pill tab-btn ${currentTab === 'global' ? 'on' : ''}" data-tab="global"
              style="flex:0 0 auto;white-space:nowrap;">Global</button>
      <button type="button" class="pill tab-btn ${currentTab === 'friends' ? 'on' : ''}" data-tab="friends"
              style="flex:0 0 auto;white-space:nowrap;">Friends</button>
      ${topicTabs}
    </div>
  `;
}

function renderBoard(entries, myUserId) {
  if (!entries || entries.length === 0) {
    return `
      <div class="card center muted" style="padding:24px 18px;">
        <div style="font-family:var(--font-display);font-size:14px;color:var(--ink);margin-bottom:6px;">
          No scores yet
        </div>
        <div style="font-size:12px;">
          Be the first to play a live trivia in this category.
        </div>
      </div>
    `;
  }
  return entries.map((e) => leaderboardRow(e, myUserId)).join('');
}

function leaderboardRow(e, myUserId) {
  const isYou = e.user_id === myUserId;
  const handle = e.username || 'Player';
  // First, second, third get the medal styling; everyone else is a plain number.
  const rankBadge = (() => {
    if (e.rank === 1) return '<div class="ic gold" style="color:#3a2a00;font-weight:700;">1</div>';
    if (e.rank === 2) return '<div class="ic" style="background:#cfd5e2;color:#2a2d36;font-weight:700;">2</div>';
    if (e.rank === 3) return '<div class="ic" style="background:#d99668;color:#2c1700;font-weight:700;">3</div>';
    return `<div class="ic">${e.rank}</div>`;
  })();
  // Phase 8.10: tap-to-profile. Self rows still route to /u/{id} — the
  // public profile resolves the viewer's relationship to "self" and
  // swaps Add/Challenge for Edit/Privacy. Avoids forking the leaderboard
  // row by viewer identity.
  const profileHref = `/u/${encodeURIComponent(e.user_id)}`;
  return `
    <a href="${profileHref}" data-link class="card pad-sm" style="display:block;text-decoration:none;color:inherit;${isYou ? 'border-color:var(--violet);' : ''}">
      <div class="row">
        ${rankBadge}
        <div class="av sm">${escape(handle.slice(0, 1).toUpperCase())}</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">
            ${escape(handle)}${isYou ? ' <span class="tag vio" style="font-size:9px;">YOU</span>' : ''}
          </b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;">
            ${e.trivias_played} trivias · ${Number(e.duels_played || 0)} duels · ${e.correct} correct
          </div>
        </div>
        <b style="font-family:var(--font-display);color:#fff;font-weight:700;">
          ${e.points}
        </b>
      </div>
    </a>
  `;
}

// ---- badges ----------------------------------------------------------------

function badgesGrid(badges) {
  if (!badges || badges.length === 0) return '';
  return `
    <h3 class="sec">Badges</h3>
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:9px;">
      ${badges.map(badgeCard).join('')}
    </div>
  `;
}

function badgeCard(b) {
  const opacity = b.earned ? '1' : '0.35';
  const border = b.earned ? 'border-color:var(--gold-t);' : '';
  return `
    <div class="card center" title="${escape(b.description)}"
         style="padding:12px 6px;${border}opacity:${opacity};">
      <div style="font-size:24px;">${escape(b.emoji)}</div>
      <div style="font-family:var(--font-display);font-size:10.5px;font-weight:600;margin-top:4px;color:#fff;">
        ${escape(b.label)}
      </div>
    </div>
  `;
}

// ---- window + tab interaction ----------------------------------------------

async function refreshBoard() {
  const host = document.getElementById('board-list');
  host.innerHTML = `<div class="skel"></div><div class="skel"></div>`;
  try {
    let path;
    if (currentTab === 'global') {
      path = `/leaderboard/global?limit=50&window=${currentWindow}`;
    } else if (currentTab === 'friends') {
      path = `/leaderboard/friends?limit=50&window=${currentWindow}`;
    } else {
      path = `/leaderboard/topic/${encodeURIComponent(currentTab)}?limit=50&window=${currentWindow}`;
    }
    const board = await api(path);
    host.innerHTML = renderBoard(board.entries, currentMe.user_id);
  } catch (err) {
    host.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
  }
}

function wireWindowClicks() {
  document.querySelectorAll('.window-btn').forEach((btn) =>
    btn.addEventListener('click', async () => {
      const win = btn.dataset.window;
      if (win === currentWindow) return;
      currentWindow = win;
      document.querySelectorAll('.window-btn').forEach((b) =>
        b.classList.toggle('on', b.dataset.window === win),
      );
      await refreshBoard();
    }),
  );
}

function wireTabClicks() {
  document.querySelectorAll('.tab-btn').forEach((btn) =>
    btn.addEventListener('click', async () => {
      const tab = btn.dataset.tab;
      if (tab === currentTab) return;
      currentTab = tab;
      document.querySelectorAll('.tab-btn').forEach((b) =>
        b.classList.toggle('on', b.dataset.tab === tab),
      );
      await refreshBoard();
    }),
  );
}
